; (function () {
  'use strict'
  const PART_CONNECT = 'bfw-pains-part-connect';
  const PART_DISCONNECT = 'bfw-pains-part-disconnect'; const PAIN_CHANGE = 'pain-change';

  const PART_TAB = 'tab';
  const PART_STEP = 'step';
  const PART_PANEL = 'panel'; const PART_SLIDES = 'slides';
  const PAIN_SLIDE = 'bfw-pains-slide';

  const STEP_PREV = -1;
  const STEP_NEXT = 1;
  const SLIDES_MEDIA = '(max-width: 767.98px)'; const SLIDES_SPEED = 320;
  class BfwPainsPart extends HTMLElement {
    get partName() {
      return '';
    }

    get partIndex() {
      return -1;
    }

    get partValid() {
      return true;
    }

    get partInteractive() {
      return true;
    }

    connectedCallback() {
      this.#announce(PART_CONNECT);
    }

    disconnectedCallback() {
      this.#announce(PART_DISCONNECT);
    }

    #announce(type) {
      this.dispatchEvent(
        new CustomEvent(type, {
          bubbles: true,
          detail: { part: this.partName, index: this.partIndex, element: this },
        }),
      );
    }
  }
  class BfwPainTab extends BfwPainsPart {
    get partName() {
      return PART_TAB;
    }

    get partIndex() {
      return readIndex(this.dataset.painIndex);
    }

    get partValid() {
      return this.partIndex >= 0;
    }
  }
  class BfwPainStep extends BfwPainsPart {
    get partName() {
      return PART_STEP;
    }

    get partIndex() {
      return Number.parseInt(this.dataset.painsStep ?? '', 10) === STEP_PREV ? STEP_PREV : STEP_NEXT;
    }
  }
  class BfwPainPanel extends BfwPainsPart {
    get partName() {
      return PART_PANEL;
    }

    get partIndex() {
      return readIndex(this.dataset.painIndex);
    }

    get partValid() {
      return this.partIndex >= 0;
    }

    get partInteractive() {
      return false;
    }
  }
  class BfwPainSlides extends BfwPainsPart {
    static get observedAttributes() {
      return ['active-index'];
    }

    #swiper = null;
    #media = globalThis.matchMedia?.(SLIDES_MEDIA) ?? null;
    #index = 0;

    get partName() {
      return PART_SLIDES;
    }

    get partInteractive() {
      return false;
    }

    connectedCallback() {
      super.connectedCallback();
      this.#index = parseIndex(this.getAttribute('active-index'));
      this.#media?.addEventListener('change', this);
      if (typeof globalThis.Swiper === 'undefined') globalThis.addEventListener('load', this);
      this.#sync();
    }

    disconnectedCallback() {
      this.#media?.removeEventListener('change', this);
      globalThis.removeEventListener('load', this);
      this.#destroy();
      super.disconnectedCallback();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      this.#index = parseIndex(newValue);
      this.#slideTo();
    }

    handleEvent(event) {
      if (event.type === 'load' || event.type === 'change') this.#sync();
    }

    #sync() {
      if (this.#media?.matches ?? true) this.#init();
      else this.#destroy();
    }

    #init() {
      const Swiper = globalThis.Swiper;
      if (!Swiper || this.#swiper || !this.isConnected) return;

      this.setAttribute('data-swiper-ready', '');

      try {
        this.#swiper = new Swiper(this, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: SLIDES_SPEED,
          initialSlide: this.#index,
          a11y: { enabled: false },
          preventClicks: true,
          on: {
            slideChange: (swiper) => this.#onSlideChange(swiper),
          },
        });
      } catch {
        this.removeAttribute('data-swiper-ready');
        return;
      }
    }

    #destroy() {
      this.#swiper?.destroy(true, true);
      this.#swiper = null;
      this.removeAttribute('data-swiper-ready');
    }

    #onSlideChange(swiper) {
      const index = Number.isInteger(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex;
      this.#index = index;
      this.dispatchEvent(
        new CustomEvent(PAIN_SLIDE, { bubbles: true, composed: true, detail: { index } }),
      );
    }

    #slideTo() {
      const swiper = this.#swiper;
      if (!swiper || swiper.destroyed || swiper.activeIndex === this.#index) return;
      swiper.slideTo(this.#index, this.#reduceMotion ? 0 : SLIDES_SPEED);
    }

    get #reduceMotion() {
      return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    }
  }
  class BfwPains extends HTMLElement {
    static get observedAttributes() {
      return ['active-index'];
    }

    #tabs = new Map();
    #steps = new Map();
    #panels = new Map();
    #slides = null;
    #desired = 0;
    #active = -1;
    #ready = false;
    #syncScheduled = false;

    connectedCallback() {
      this.addEventListener(PART_CONNECT, this);
      this.addEventListener(PART_DISCONNECT, this);
      this.addEventListener(PAIN_SLIDE, this);
      this.addEventListener('click', this);
      this.addEventListener('mousedown', this);
      this.addEventListener('keydown', this);

      this.#sync(this.#readIndex());
      this.#ready = true;
    }

    disconnectedCallback() {
      this.removeEventListener(PART_CONNECT, this);
      this.removeEventListener(PART_DISCONNECT, this);
      this.removeEventListener(PAIN_SLIDE, this);
      this.removeEventListener('click', this);
      this.removeEventListener('mousedown', this);
      this.removeEventListener('keydown', this);

      this.#tabs.clear();
      this.#steps.clear();
      this.#panels.clear();
      this.#slides = null;
      this.#active = -1;
      this.#ready = false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'active-index' || oldValue === newValue || !this.isConnected) return;
      this.#sync(parseIndex(newValue));
    }

    get activeIndex() {
      return this.#active;
    }

    select(index) {
      const next = this.#clamp(index);
      if (next === this.#active) return;
      this.setAttribute('active-index', String(next));
    }

    handleEvent(event) {
      switch (event.type) {
        case PART_CONNECT:
          this.#register(event.detail);
          break;
        case PART_DISCONNECT:
          this.#unregister(event.detail);
          break;
        case PAIN_SLIDE:
          this.select(event.detail.index);
          break;
        case 'click':
          this.#onActivate(event);
          break;
        case 'mousedown':
          this.#onMousedown(event);
          break;
        case 'keydown':
          this.#onKeydown(event);
          break;
        default:
          break;
      }
    }

    #register({ part, index, element }) {
      if (!element) return;
      if (part === PART_SLIDES) {
        this.#slides = element;
      } else if (part === PART_TAB || part === PART_PANEL) {
        if (index < 0) return;
        this.#registry(part).set(index, element);
      } else if (part === PART_STEP) {
        this.#steps.set(index, element);
      } else {
        return;
      }

      if (!this.#syncScheduled) {
        this.#syncScheduled = true;
        queueMicrotask(() => {
          this.#syncScheduled = false;
          if (this.isConnected) this.#sync(this.#desired, true);
        });
      }
    }

    #unregister({ part, index, element }) {
      if (part === PART_SLIDES) {
        if (this.#slides === element) this.#slides = null;
        return;
      }
      const registry = this.#registry(part);
      if (registry && registry.get(index) === element) registry.delete(index);
    }

    #registry(part) {
      if (part === PART_TAB) return this.#tabs;
      if (part === PART_PANEL) return this.#panels;
      if (part === PART_STEP) return this.#steps;
      return null;
    }

    #onActivate(event) {
      const part = this.#partFrom(event);
      if (part) this.#activate(part);
    }

    #onMousedown(event) {
      if (event.detail > 1 && this.#partFrom(event)) event.preventDefault();
    }

    #onKeydown(event) {
      const part = this.#partFrom(event);
      if (!part) return;

      const onTab = part.partName === PART_TAB;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          if (onTab) this.#move(part.partIndex + 1);
          else this.select(this.#active + 1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          if (onTab) this.#move(part.partIndex - 1);
          else this.select(this.#active - 1);
          break;
        case 'Home':
          if (!onTab) return;
          event.preventDefault();
          this.#move(0);
          break;
        case 'End':
          if (!onTab) return;
          event.preventDefault();
          this.#move(this.#maxIndex);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.#activate(part);
          break;
        default:
          break;
      }
    }

    #activate(part) {
      if (part.partName === PART_TAB) {
        this.select(part.partIndex);
        return;
      }
      this.select(this.#active + part.partIndex);
    }

    #move(index) {
      const next = this.#clamp(index);
      this.select(next);
      this.#tabs.get(next)?.focus({ preventScroll: true });
    }

    #partFrom(event) {
      for (const node of event.composedPath()) {
        if (!(node instanceof BfwPainsPart) || !node.partValid || !node.partInteractive) continue;
        if (node.partName === PART_TAB) this.#tabs.set(node.partIndex, node);
        return node;
      }
      return null;
    }

    #sync(value, silent = false) {
      this.#desired = Number.isFinite(value) ? Math.trunc(value) : 0;

      const next = this.#clamp(this.#desired);
      const changed = next !== this.#active;
      this.#active = next;

      const reflected = String(next);
      if (this.getAttribute('active-index') !== reflected) this.setAttribute('active-index', reflected);

      this.style.setProperty('--active-i', reflected);

      for (const [index, tab] of this.#tabs) {
        const selected = index === next;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      }

      for (const [index, panel] of this.#panels) {
        const active = index === next;
        panel.setAttribute('data-active', String(active));
        panel.toggleAttribute('inert', !active);
      }

      this.#slides?.setAttribute('active-index', reflected);

      const last = this.#maxIndex;
      for (const [step, element] of this.#steps) {
        const blocked = step === STEP_PREV ? next <= 0 : next >= last;
        element.setAttribute('aria-disabled', String(blocked));
      }

      if (!changed || silent || !this.#ready) return;

      const tab = this.#tabs.get(next);
      this.setAttribute('data-reveal', String(next));

      const box = tab?.parentElement;
      const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const reveal = (behavior) => {
        if (!tab || !box) return;
        const vertical = box.scrollHeight - box.clientHeight > box.scrollWidth - box.clientWidth;
        const pos = vertical ? box.scrollTop : box.scrollLeft;
        const view = vertical ? box.clientHeight : box.clientWidth;
        const start = vertical ? tab.offsetTop : tab.offsetLeft;
        const len = vertical ? tab.offsetHeight : tab.offsetWidth;
        const max = Math.max(0, (vertical ? box.scrollHeight : box.scrollWidth) - view);
        let target = pos;
        if (vertical) {
          if (start < pos) target = start;
          else if (start + len > pos + view) target = start + len - view;
        } else {
          target = start + len / 2 - view / 2;
        }
        target = Math.min(Math.max(target, 0), max);
        if (target === pos) return;
        if (vertical) box.scrollTo({ top: target, behavior });
        else box.scrollTo({ left: target, behavior });
      };

      reveal(reduceMotion ? 'auto' : 'smooth');

      const vertical = !!box && box.scrollHeight - box.clientHeight > box.scrollWidth - box.clientWidth;
      if (vertical && !reduceMotion) {
        Promise.all((tab?.getAnimations?.() ?? []).map((animation) => animation.finished))
          .then(() => {
            if (this.#active === next) reveal('smooth');
          })
          .catch(() => { });
      }

      this.dispatchEvent(
        new CustomEvent(PAIN_CHANGE, {
          bubbles: true,
          composed: true,
          detail: { index: next, element: tab ?? null },
        }),
      );
    }

    #readIndex() {
      return parseIndex(this.getAttribute('active-index'));
    }

    #clamp(value) {
      const index = Number.isFinite(value) ? Math.trunc(value) : 0;
      const last = this.#maxIndex;
      const safe = Math.max(index, 0);
      return last < 0 ? safe : Math.min(safe, last);
    }

    get #maxIndex() {
      if (this.#tabs.size === 0) return -1;
      return Math.max(...this.#tabs.keys());
    }
  }

  function parseIndex(value) {
    const index = Number.parseInt(value ?? '', 10);
    return Number.isInteger(index) ? index : 0;
  }

  function readIndex(value) {
    const index = Number.parseInt(value ?? '', 10);
    return Number.isInteger(index) && index >= 0 ? index : -1;
  }
  if (!customElements.get('bfw-pains')) customElements.define('bfw-pains', BfwPains);
  if (!customElements.get('bfw-pain-tab')) customElements.define('bfw-pain-tab', BfwPainTab);
  if (!customElements.get('bfw-pain-step')) customElements.define('bfw-pain-step', BfwPainStep);
  if (!customElements.get('bfw-pain-panel')) customElements.define('bfw-pain-panel', BfwPainPanel);
  if (!customElements.get('bfw-pain-slides')) customElements.define('bfw-pain-slides', BfwPainSlides);
})()

  ; (function () {
    'use strict'

    if (window.__bfwStripBooted) return
    window.__bfwStripBooted = true

    var STORAGE_KEY = 'bfw:wishlist'
    var DATA_SCRIPT_ID = 'bfw-wishlist-data'
    var SLOT_SELECTOR = '[data-bfw-slot]'
    var CTA_SELECTOR = '[data-bfw-strip-cta]'
    var I18N_SELECTOR = '[data-bfw-strip-i18n]'
    var CARD_SELECTOR = '.bfw__card[data-product-id]'
    var CARD_BTN_SELECTOR = '.bfw__card-btn'
    var WISH_SLOTS_MIN = 3
    var WISH_GROUP_SELECTOR = '[data-bfw-wish-group]'
    var WISH_SEP_SELECTOR = '.bfw__wish-sep'
    var WISH_MODAL_SELECTOR = '[data-dsm-dialog="bfw-wish-modal"]'
    var WISH_EDIT_SELECTOR = '[data-bfw-wish-edit]'
    var WISH_CTA_SELECTOR = '[data-bfw-wish-cta]'
    var MAX_PRODUCTS = 4
    var UNLOCK_PRODUCTS = 3
    var LEGACY_CREATED_KEY = 'bfw:wishlist:created'
    var NOTE_SELECTOR = '[data-bfw-strip-note]'
    var RECORD_SELECTOR = '[data-bfw-strip-record]'
    var RECORD_HEAD_SELECTOR = '[data-bfw-strip-record-head]'
    var EDIT_SELECTOR = '[data-bfw-strip-edit]'
    var SLOT_REMOVE_SELECTOR = '[data-bfw-slot-remove]'
    var WISH_ROW_REMOVE_SELECTOR = '[data-bfw-wish-row-remove]'
    var GIFTS_SELECTOR = '[data-bfw-gifts]'
    var GIFTS_TEXT_SELECTOR = '.bfw__gifts-text'
    var TOAST_SELECTOR = '[data-bfw-toast]'
    var TOAST_TEXT_SELECTOR = '[data-bfw-toast-text]'
    var TOAST_DURATION = 3000
    var TOAST_FADE = 160
    var CTA_LABEL_BY_STATE = { selecting: 'idle', ready: 'ready', created: 'created', editing: 'update' }

    var REWARD_MARK_SELECTOR = '[data-bfw-reward-scope][data-bfw-reward-kind]'
    var REWARD_DIALOG_FALLBACK = {
      created: {
        bundle: 'bfw-reward-created-bundle',
        'gift-card': 'bfw-reward-created-gift-card',
      },
      record: {
        bundle: 'bfw-reward-record-bundle',
        'gift-card': 'bfw-reward-record-gift-card',
      },
      duplicate: 'bfw-reward-already',
    }

    var createPending = false

    var submitPending = false
    var hydratePending = false

    var rewardKind = ''

    var rewardsResolved = false
    var recordPending = false

    var EVENTS = {
      toggle: 'bfw:wishlist:toggle',
      add: 'bfw:wishlist:add',
      remove: 'bfw:wishlist:remove',
      clear: 'bfw:wishlist:clear',
      change: 'bfw:wishlist:change',
      create: 'bfw:wishlist:create',
      update: 'bfw:wishlist:update',
      edit: 'bfw:wishlist:edit',
      error: 'bfw:wishlist:error',
    }

    var catalog = { products: {}, variant_index: {}, money_format: '' }
    var catalogReady = false
    var debug = false

    function log() {
      if (!debug || !window.console) return
      console.log.apply(console, ['[bfw-strip]'].concat([].slice.call(arguments)))
    }

    function emit(name, detail, target, cancelable) {
      var event = new CustomEvent(name, { bubbles: true, cancelable: !!cancelable, detail: detail || {} })
        ; (target || document).dispatchEvent(event)
      return event
    }

    function toId(value) {
      if (value === null || value === undefined || value === '') return 0
      var id = parseInt(String(value).replace(/\D/g, ''), 10)
      return isFinite(id) && id > 0 ? id : 0
    }

    function toQty(value) {
      var qty = parseInt(value, 10)
      if (!qty || qty < 1) qty = 1
      return qty > 9999 ? 9999 : qty
    }

    function pidKey(value) {
      return value === null || value === undefined ? '' : String(value).trim()
    }

    function toHandle(value) {
      if (typeof value !== 'string') return ''
      var handle = value.trim()
      return handle && handle.length <= 255 ? handle : ''
    }

    function normalize(raw) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
      var clean = Object.create(null)
      Object.keys(raw).forEach(function (pid) {
        var key = pidKey(pid)
        var list = raw[pid]
        if (!key || !Array.isArray(list)) return

        var items = []
        list.forEach(function (entry) {
          if (!entry || typeof entry !== 'object') return
          var uid = toId(entry.uid)
          if (!uid) return
          var handle = toHandle(entry.handle)
          for (var i = 0; i < items.length; i++) {
            if (items[i].uid === uid) {
              items[i].qty = toQty(entry.qty)
              if (!items[i].handle && handle) items[i].handle = handle
              return
            }
          }
          var item = { uid: uid, qty: toQty(entry.qty) }
          if (handle) item.handle = handle
          items.push(item)
        })
        if (items.length) clean[key] = items
      })
      return clean
    }

    function legacyCreated() {
      try {
        return window.localStorage.getItem(LEGACY_CREATED_KEY) === '1'
      } catch (error) {
        return false
      }
    }

    function readState() {
      var parsed = null
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY)
        parsed = raw ? JSON.parse(raw) : null
      } catch (error) {
        emit(EVENTS.error, { reason: 'storage-read-failed', detail: String(error) })
        return { items: {}, created: false, account: '' }
      }
      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        parsed.items &&
        typeof parsed.items === 'object' &&
        !Array.isArray(parsed.items)
      ) {
        return {
          items: normalize(parsed.items),
          created: !!parsed.created,
          account: typeof parsed.account === 'string' ? parsed.account : '',
        }
      }
      return { items: normalize(parsed), created: legacyCreated(), account: '' }
    }

    function writeState(state) {
      try {
        var account = state.account === undefined ? readState().account : String(state.account || '')
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ items: state.items, created: !!state.created, account: account })
        )
        window.localStorage.removeItem(LEGACY_CREATED_KEY)
        return true
      } catch (error) {
        emit(EVENTS.error, { reason: 'storage-write-failed', detail: String(error) })
        return false
      }
    }

    function currentAccount() {
      var info = window.__MOMCOZY_USER_INFO__ || {}
      return String(info.email || '').trim().toLowerCase()
    }

    function syncAccount() {
      var account = currentAccount()
      if (!account) return false

      var state = readState()
      if (!state.account) {
        writeState({ items: state.items, created: state.created, account: account })
        return false
      }
      if (state.account === account) return false

      rewardKind = ''
      writeState({ items: {}, created: false, account: account })
      renderStrips()
      emit(EVENTS.change, { data: {}, items: [], count: 0, source: 'account-switch' })
      log('account-switch', { from: state.account, to: account })
      return true
    }

    function read() {
      return readState().items
    }

    function write(data) {
      return writeState({ items: data, created: isCreated() })
    }

    function isCreated() {
      return readState().created
    }

    function getRewardKind() {
      return rewardKind
    }

    function setRewardKind(kind) {
      rewardKind = kind || ''
      return true
    }

    function setCreated(value) {
      if (!value) rewardKind = ''
      return writeState({ items: read(), created: value })
    }

    function renderStrips() {
      var strips = document.querySelectorAll('bfw-strip')
      for (var i = 0; i < strips.length; i++) {
        if (typeof strips[i].render === 'function') strips[i].render()
      }
    }

    function rewardMarks() {
      return [].slice.call(document.querySelectorAll(REWARD_MARK_SELECTOR))
    }

    function listRewardKinds() {
      var kinds = []
      rewardMarks().forEach(function (mark) {
        var scope = mark.getAttribute('data-bfw-reward-scope')
        var kind = mark.getAttribute('data-bfw-reward-kind')
        if (scope !== 'created' || !kind || kinds.indexOf(kind) !== -1) return
        kinds.push(kind)
      })
      return kinds
    }

    function findRewardDialog(scope, kind) {
      var marks = rewardMarks()
      var sameScope = []
      for (var i = 0; i < marks.length; i++) {
        var mark = marks[i]
        if (mark.getAttribute('data-bfw-reward-scope') !== scope) continue
        var dialog = mark.closest ? mark.closest('[data-dsm-dialog]') : null
        if (!dialog) continue
        var id = dialog.getAttribute('data-dsm-dialog') || ''
        if (!id) continue
        if (kind && mark.getAttribute('data-bfw-reward-kind') === kind) return id
        sameScope.push(id)
      }
      if (sameScope.length) return sameScope[0]
      if (scope === 'duplicate') return REWARD_DIALOG_FALLBACK.duplicate
      var fallback = REWARD_DIALOG_FALLBACK[scope] || REWARD_DIALOG_FALLBACK.created
      return fallback[kind] || fallback.bundle
    }

    function openRewardDialog(kind, scope) {
      var id = findRewardDialog(scope || 'created', kind)
      if (!window.DsmDialog || typeof window.DsmDialog.open !== 'function') {
        emit(EVENTS.error, { reason: 'reward-dialog-unavailable', detail: id })
        return ''
      }
      window.DsmDialog.open(id)
      return id
    }

    function completeCreate(opts) {
      opts = opts || {}
      var kind = opts.duplicate ? 'duplicate' : opts.kind || listRewardKinds()[0] || 'bundle'
      var scope = opts.duplicate ? 'duplicate' : 'created'
      createPending = false
      submitPending = false
      if (!opts.duplicate) setRewardKind(kind)
      setCreated(true)
      renderStrips()
      return openRewardDialog(kind, scope)
    }

    function failCreate() {
      createPending = false
      submitPending = false
      renderStrips()
    }

    function count(data) {
      var source = data || read()
      return Object.keys(source).reduce(function (total, pid) {
        return total + source[pid].length
      }, 0)
    }

    function countProducts(data) {
      return Object.keys(data || read()).length
    }

    function hasProduct(pid) {
      var key = pidKey(pid)
      return !!(key && read()[key])
    }

    function isUnlocked(data) {
      return countProducts(data) >= UNLOCK_PRODUCTS
    }

    function isFull(data) {
      return countProducts(data) >= MAX_PRODUCTS
    }

    function afterChange(type, payload, data) {
      var detail = {
        type: type,
        pid: payload.pid || '',
        uid: payload.uid || 0,
        qty: payload.qty || 0,
        source: payload.source || 'unknown',
        data: data,
        items: getItems(data),
        count: count(data),
      }
      emit(EVENTS.change, detail)
      log(type, detail)
      return detail
    }

    function add(pid, uid, qty, source, handle) {
      var key = pidKey(pid)
      var variantId = toId(uid)
      if (!key || !variantId) {
        emit(EVENTS.error, { reason: 'missing-pid-or-uid', detail: { pid: pid, uid: uid } })
        return null
      }

      var handleKey = toHandle(handle) || catalogHandle(key)
      var data = read()
      if (!data[key] && countProducts(data) >= MAX_PRODUCTS) {
        emit(EVENTS.error, {
          reason: 'products-limit-reached',
          detail: { pid: key, uid: variantId, max: MAX_PRODUCTS },
        })
        return null
      }

      var list = data[key] || []
      var next = toQty(qty)
      var found = false
      var type = 'add'

      for (var i = 0; i < list.length; i++) {
        if (list[i].uid === variantId) {
          found = true
          type = 'update'
          list[i].qty = toQty(toQty(list[i].qty) + next)
          if (!list[i].handle && handleKey) list[i].handle = handleKey
          break
        }
      }
      if (!found) {
        list.push(handleKey ? { uid: variantId, qty: next, handle: handleKey } : { uid: variantId, qty: next })
      }

      data[key] = list
      if (!write(data)) return null
      return afterChange(type, { pid: key, uid: variantId, qty: next, source: source }, data)
    }

    function remove(pid, uid, source) {
      var key = pidKey(pid)
      var variantId = toId(uid)
      if (!key || !variantId) {
        emit(EVENTS.error, { reason: 'missing-pid-or-uid', detail: { pid: pid, uid: uid } })
        return null
      }

      var data = read()
      var list = data[key]
      if (!list) return null

      var next = list.filter(function (entry) {
        return entry.uid !== variantId
      })
      if (next.length === list.length) return null

      if (next.length) data[key] = next
      else delete data[key]

      if (!write(data)) return null
      return afterChange('remove', { pid: key, uid: variantId, source: source }, data)
    }

    function removeProduct(pid, source) {
      var key = pidKey(pid)
      if (!key) {
        emit(EVENTS.error, { reason: 'missing-pid-or-uid', detail: { pid: pid } })
        return null
      }

      var data = read()
      if (!data[key]) return null
      delete data[key]

      if (!write(data)) return null
      return afterChange('remove', { pid: key, source: source }, data)
    }

    function toggle(pid, uid, qty, source, handle) {
      return has(pid, uid) ? remove(pid, uid, source) : add(pid, uid, qty, source, handle)
    }

    function clear(source) {
      if (!count()) return null
      if (!write({})) return null
      return afterChange('clear', { source: source }, {})
    }

    function has(pid, uid) {
      var key = pidKey(pid)
      var variantId = toId(uid)
      if (!key || !variantId) return false
      var list = read()[key]
      if (!list) return false
      return list.some(function (entry) {
        return entry.uid === variantId
      })
    }

    function setItems(raw, source) {
      var data = normalize(raw)
      var current = read()
      Object.keys(data).forEach(function (pid) {
        var previous = current[pid]
        if (!previous) return
        data[pid].forEach(function (entry) {
          if (entry.handle) return
          for (var i = 0; i < previous.length; i++) {
            if (previous[i].uid === entry.uid && previous[i].handle) {
              entry.handle = previous[i].handle
              return
            }
          }
        })
      })
      if (!write(data)) return null
      return afterChange('set', { source: source || 'setItems' }, data)
    }

    function getPayload() {
      var data = read()
      var items = []
      Object.keys(data).forEach(function (pid) {
        data[pid].forEach(function (entry) {
          items.push({ pid: pid, uid: entry.uid, qty: entry.qty })
        })
      })
      return { items: items, count: items.length }
    }

    function refreshCatalog() {
      catalogReady = false
      catalog = { products: {}, variant_index: {}, groups: [], money_format: '' }
      var el = document.getElementById(DATA_SCRIPT_ID)
      if (!el) return catalog
      try {
        var json = JSON.parse(el.textContent || '{}')
        catalog = {
          products: json.products && typeof json.products === 'object' ? json.products : {},
          variant_index: json.variant_index && typeof json.variant_index === 'object' ? json.variant_index : {},
          groups: Array.isArray(json.groups) ? json.groups : [],
          money_format: json.money_format || '',
        }
      } catch (error) {
        emit(EVENTS.error, { reason: 'catalog-parse-failed', detail: String(error) })
      }
      catalogReady = true
      return catalog
    }

    function ensureCatalog() {
      if (!catalogReady) refreshCatalog()
      return catalog
    }

    function pidByVariant(variantId) {
      var uid = toId(variantId)
      if (!uid) return ''
      return pidKey(ensureCatalog().variant_index[String(uid)] || '')
    }

    function catalogHandle(pid) {
      var product = ensureCatalog().products[pidKey(pid)]
      return product ? toHandle(product.handle) : ''
    }

    function formatMoney(cents) {
      if (cents === null || cents === undefined || cents === '') return ''
      var value = Number(cents)
      if (!isFinite(value)) return ''

      var currency = window.T4SThemeSP && window.T4SThemeSP.Currency
      if (currency && typeof currency.formatMoney === 'function') {
        try {
          var formatted = currency.formatMoney(value)
          if (formatted) return formatted
        } catch (error) {
        }
      }

      var format = ensureCatalog().money_format || '${{amount}}'
      var token = format.match(/\{\{\s*(\w+)\s*\}\}/)
      var name = token ? token[1] : 'amount'
      var noDecimals = name.indexOf('no_decimals') > -1
      var commaStyle = name.indexOf('comma_separator') > -1
      var apostropheStyle = name.indexOf('apostrophe_separator') > -1

      var decimals = noDecimals ? 0 : 2
      var parts = (value / 100).toFixed(decimals).split('.')
      var thousandSep = apostropheStyle ? '’' : commaStyle ? '.' : ','
      var decimalSep = commaStyle ? ',' : '.'
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep)

      var amount = decimals ? parts.join(decimalSep) : parts[0]
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount)
    }

    function getItems(data) {
      var source = data || read()
      var products = ensureCatalog().products
      var items = []

      Object.keys(source).forEach(function (pid) {
        var product = products[pid] || null
        var variants = product && product.variants ? product.variants : {}
        var multiVariant = Object.keys(variants).length > 1

        source[pid].forEach(function (entry) {
          var variant = variants[String(entry.uid)] || null
          var price = variant ? variant.price : product ? product.price : null
          items.push({
            pid: pid,
            uid: entry.uid,
            qty: entry.qty,
            handle: entry.handle || (product ? toHandle(product.handle) : ''),
            title: product ? product.title : '',
            variant_title: multiVariant && variant ? variant.title : '',
            url: product ? product.url : '',
            image: (variant && variant.image) || (product ? product.image : ''),
            price: price,
            price_html: price === null || price === undefined ? '' : formatMoney(price),
            available: variant ? variant.available !== false : product ? product.available !== false : true,
            product: product,
            variant: variant,
          })
        })
      })

      return items
    }

    function groupByProduct(items) {
      var groups = []
      var byPid = {}

      items.forEach(function (item) {
        var group = byPid[item.pid]
        if (!group) {
          group = {
            pid: item.pid,
            items: [],
            uids: [],
            qty: 0,
            image: '',
            handle: item.handle || '',
            title: item.title || '',
            price_cents: null,
            price_html: '',
            detailLabel: '',
          }
          byPid[item.pid] = group
          groups.push(group)
        }
        group.items.push(item)
        group.uids.push(item.uid)
        group.qty += item.qty
        if (!group.image && item.image) group.image = item.image
        if (!group.handle && item.handle) group.handle = item.handle
        if (!group.title && item.title) group.title = item.title
        if (typeof item.price === 'number' && (group.price_cents === null || item.price < group.price_cents)) {
          group.price_cents = item.price
        }
      })

      groups.forEach(function (group) {
        group.price_html = group.price_cents === null ? '' : formatMoney(group.price_cents)
        group.detailLabel = group.items
          .map(function (item) {
            var parts = []
            var name = item.variant_title || group.title || ''
            if (name) parts.push(name)
            if (item.price_html) parts.push(item.price_html)
            if (item.qty > 1) parts.push('x' + item.qty)
            return parts.join(' ')
          })
          .filter(Boolean)
          .join('、')
      })

      return groups
    }

    function buildWishSlot(group, removeLabel, closable) {
      var row = document.createElement('li')
      row.className = 'bfw__wish-row'
      row.setAttribute('data-pid', group.pid)
      row.setAttribute('data-uids', group.uids.join(','))
      row.setAttribute('data-qty', String(group.qty))
      row.setAttribute('data-variant-count', String(group.items.length))
      if (group.detailLabel) row.setAttribute('title', group.detailLabel)

      var thumb = document.createElement('span')
      thumb.className = 'bfw__wish-row-thumb'
      if (group.image) {
        var img = document.createElement('img')
        img.src = group.image
        img.alt = group.title || ''
        img.loading = 'lazy'
        thumb.appendChild(img)
      }
      row.appendChild(thumb)

      var bar = document.createElement('span')
      bar.className = 'bfw__wish-row-bar'

      var price = document.createElement('span')
      price.className = 'bfw__wish-row-price'
      price.textContent = group.price_html || ''
      bar.appendChild(price)

      var qty = document.createElement('span')
      qty.className = 'bfw__wish-row-qty'
      qty.textContent = group.qty > 0 ? '×' + group.qty : ''
      bar.appendChild(qty)

      row.appendChild(bar)

      var remove = document.createElement('button')
      remove.className = 'bfw__wish-row-remove'
      remove.type = 'button'
      remove.setAttribute('data-bfw-wish-row-remove', '')
      var name = (group.title || '').replace(/\s+/g, ' ').trim()
      remove.setAttribute('aria-label', name ? removeLabel + ': ' + name : removeLabel)
      remove.hidden = !closable
      var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      icon.setAttribute('viewBox', '0 0 24 24')
      icon.setAttribute('focusable', 'false')
      icon.setAttribute('aria-hidden', 'true')
        ;[[18, 6, 6, 18], [6, 6, 18, 18]].forEach(function (points) {
          var line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
          line.setAttribute('x1', String(points[0]))
          line.setAttribute('y1', String(points[1]))
          line.setAttribute('x2', String(points[2]))
          line.setAttribute('y2', String(points[3]))
          icon.appendChild(line)
        })
      remove.appendChild(icon)
      row.appendChild(remove)

      return row
    }

    function onWishEdit(event) {
      event.preventDefault()
      var strips = document.querySelectorAll('bfw-strip')
      for (var i = 0; i < strips.length; i++) {
        var strip = strips[i]
        if (strip.state !== 'created') continue
        strip._editing = true
        emit(EVENTS.edit, { data: read(), items: getItems(), count: count(), source: 'wish-modal-edit' }, strip)
        strip.render()
      }
    }

    function onSlotRemove(event) {
      var btn = event.target && event.target.closest ? event.target.closest(SLOT_REMOVE_SELECTOR) : null
      if (!btn) return
      var slot = btn.closest(SLOT_SELECTOR)
      if (!slot) return
      event.preventDefault()

      var pid = slot.getAttribute('data-pid')
      if (!pid) return
      removeProduct(pid, 'strip-slot-close')
    }

    function onDocumentClick(event) {
      var target = event.target
      if (!target || !target.closest) return
      if (target.closest(WISH_CTA_SELECTOR)) onWishCta(event)
      else if (target.closest(WISH_EDIT_SELECTOR)) onWishEdit(event)
      else if (target.closest(SLOT_REMOVE_SELECTOR)) onSlotRemove(event)
      else if (target.closest(WISH_ROW_REMOVE_SELECTOR)) onWishRowRemove(event)
    }

    function onWishCta(event) {
      event.preventDefault()
      var strips = document.querySelectorAll('bfw-strip')
      for (var i = 0; i < strips.length; i++) {
        var strip = strips[i]
        if (strip.state === 'selecting') continue
        strip.submitCta(null, 'wish-modal-cta')
        return
      }
    }

    function onWishRowRemove(event) {
      var btn = event.target && event.target.closest ? event.target.closest(WISH_ROW_REMOVE_SELECTOR) : null
      if (!btn) return
      var row = btn.closest('.bfw__wish-row')
      if (!row) return
      event.preventDefault()

      var pid = row.getAttribute('data-pid')
      if (!pid) return
      removeProduct(pid, 'wish-modal-row-close')
    }

    function onCardButtonClick(event) {
      var target = event.target
      if (!target || !target.closest) return
      var btn = target.closest(CARD_BTN_SELECTOR)
      if (!btn) return

      if (btn.classList.contains('is--soldout')) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (!btn.classList.contains('is--disabled')) return

      event.preventDefault()
      event.stopPropagation()

      var strips = document.querySelectorAll('bfw-strip')
      for (var i = 0; i < strips.length; i++) {
        if (typeof strips[i].showFullToast === 'function') strips[i].showFullToast()
      }
    }

    function onVariantUpdate(event) {
      var detail = event.detail || {}
      var uid = toId(detail.variantId)
      if (!uid) return

      var picker = event.target && event.target.closest ? event.target.closest('[data-quickshop]') : null

      if (detail.available === false) {
        emit(EVENTS.error, { reason: 'variant-sold-out', detail: { variantId: uid, qty: detail.qty } }, picker)
        return
      }
      var pid = picker ? pidKey(picker.getAttribute('data-product-id')) : ''
      if (!pid) pid = pidByVariant(uid)
      if (!pid) {
        emit(EVENTS.error, { reason: 'missing-pid', detail: { variantId: uid, qty: detail.qty } }, picker)
        return
      }

      var payload = {
        pid: pid,
        uid: uid,
        qty: detail.qty,
        source: 'quickshop-modal',
        handle: toHandle(detail.handle) || catalogHandle(pid),
      }
      log('quickshop confirm', payload)
      emit(EVENTS.add, payload, picker || document)
    }

    function onWishlistEvent(event) {
      var detail = event.detail || {}
      switch (event.type) {
        case EVENTS.toggle:
          toggle(detail.pid, detail.uid, detail.qty, detail.source, detail.handle)
          break
        case EVENTS.add:
          add(detail.pid, detail.uid, detail.qty, detail.source, detail.handle)
          break
        case EVENTS.remove:
          remove(detail.pid, detail.uid, detail.source)
          break
        case EVENTS.clear:
          clear(detail.source)
          break
      }
    }

    function decodeEntities(text) {
      if (text == null) return ''
      var out = String(text)
      if (out.indexOf('&') === -1) return out
      var guard = 0
      while (guard++ < 3 && /&(amp|lt|gt|quot|#39|#x27|nbsp);/i.test(out)) {
        out = out
          .replace(/&amp;/gi, '&')
          .replace(/&lt;/gi, '<')
          .replace(/&gt;/gi, '>')
          .replace(/&quot;/gi, '"')
          .replace(/&#39;|&#x27;/gi, "'")
          .replace(/&nbsp;/gi, ' ')
      }
      return out
    }

    function readStripI18n(host) {
      var node = host.querySelector(I18N_SELECTOR)
      if (!node) return {}
      try {
        return JSON.parse(node.textContent) || {}
      } catch (error) {
        console.warn('[bfw-strip] 文案 JSON 解析失败', error)
        return {}
      }
    }

    function setSubmitPending(value) {
      var next = !!value
      if (submitPending === next) return
      submitPending = next
      renderStrips()
    }

    function finishUpdate() {
      var strips = document.querySelectorAll('bfw-strip')
      for (var i = 0; i < strips.length; i++) strips[i]._editing = false
      setSubmitPending(false)
      renderStrips()
      for (var j = 0; j < strips.length; j++) {
        if (typeof strips[j].showToast === 'function') strips[j].showToast()
      }
    }

    function updateCta(button, label, enabled, state, dim) {
      var labelEl = button.querySelector('.dsm-btn__label')
      if (labelEl && label) labelEl.textContent = label
      var loading = createPending || submitPending || hydratePending
      button.classList.toggle('is-loading', loading)
      button.classList.toggle('dsm-btn--disabled', !!dim || loading)
      if (enabled && !loading) {
        button.removeAttribute('disabled')
        button.removeAttribute('aria-disabled')
      } else {
        button.setAttribute('disabled', 'disabled')
        button.setAttribute('aria-disabled', 'true')
      }
      if (loading) button.setAttribute('aria-busy', 'true')
      else button.removeAttribute('aria-busy')
    }

    class BfwStrip extends HTMLElement {
      connectedCallback() {
        if (this._bfwReady) return
        this._bfwReady = true

        this.cta = this.querySelector(CTA_SELECTOR)
        var i18n = readStripI18n(this)
        var ctaLabels = i18n.ctaLabels || {}
        var noteLabels = i18n.noteLabels || []
        this.slotLabel = decodeEntities(i18n.slotLabel) || ''
        this.createHref = i18n.createHref || ''
        this.removeLabel = decodeEntities(i18n.removeLabel) || 'Remove'
        this.ctaLabels = {
          idle: decodeEntities(ctaLabels.idle),
          ready: decodeEntities(ctaLabels.ready),
          created: decodeEntities(ctaLabels.created),
          update: decodeEntities(ctaLabels.update),
        }
        this.noteLabels = [
          decodeEntities(noteLabels[0]),
          decodeEntities(noteLabels[1]),
          decodeEntities(noteLabels[2]),
          decodeEntities(noteLabels[3]),
        ]
        this.state = 'selecting'
        this._editing = false
        this.syncCreated()
        this.toast = this.querySelector(TOAST_SELECTOR)
        this.toastText = this.toast ? this.toast.querySelector(TOAST_TEXT_SELECTOR) : null
        this.toastLabel = decodeEntities(i18n.toastText) || ''
        this.toastFullLabel = decodeEntities(i18n.toastFullText) || ''
        this.toastErrorLabel = decodeEntities(i18n.toastErrorText) || ''
        this._toastTimer = 0
        this._toastHideTimer = 0

        this._onChange = function () {
          this.syncCreated()
          this.render()
        }.bind(this)
        document.addEventListener(EVENTS.change, this._onChange)

        this._onStorage = function (event) {
          if (!event.key || event.key === STORAGE_KEY) {
            catalogReady = false
            this.syncCreated()
            this.render()
          }
        }.bind(this)
        window.addEventListener('storage', this._onStorage)

        if (this.cta) {
          this._onCta = function (event) {
            this.submitCta(event, 'strip-cta')
          }.bind(this)
          this.cta.addEventListener('click', this._onCta)
        }

        this.editBtn = this.querySelector(EDIT_SELECTOR)
        if (this.editBtn) {
          this._onEdit = function (event) {
            event.preventDefault()
            if (this.state === 'editing') {
              this._editing = false
              this.render()
              return
            }
            if (this.state !== 'created') return
            this._editing = true
            emit(EVENTS.edit, { data: read(), items: getItems(), count: count(), source: 'strip-edit' }, this)
            this.render()
          }.bind(this)
          this.editBtn.addEventListener('click', this._onEdit)
        }

        this.render()
      }

      disconnectedCallback() {
        document.removeEventListener(EVENTS.change, this._onChange)
        window.removeEventListener('storage', this._onStorage)
        if (this.cta && this._onCta) this.cta.removeEventListener('click', this._onCta)
        if (this.editBtn && this._onEdit) this.editBtn.removeEventListener('click', this._onEdit)
        if (this._toastTimer) window.clearTimeout(this._toastTimer)
        if (this._toastHideTimer) window.clearTimeout(this._toastHideTimer)
        this._toastTimer = 0
        this._toastHideTimer = 0
        this._bfwReady = false
      }

      syncCreated() {
        if (this._editing) return
        if (!isCreated()) return
        if (countProducts(read()) > 0) return
        setCreated(false)
      }

      getState(unlocked) {
        if (!isCreated()) {
          this._editing = false
          return unlocked ? 'ready' : 'selecting'
        }
        return this._editing ? 'editing' : 'created'
      }

      render() {
        var items = getItems()
        var total = items.length
        var groups = groupByProduct(items)
        var productCount = groups.length
        var unlocked = productCount >= UNLOCK_PRODUCTS
        var full = productCount >= MAX_PRODUCTS
        var state = this.getState(unlocked)
        var pidSet = Object.create(null)
        for (var i = 0; i < groups.length; i++) pidSet[groups[i].pid] = true

        this.state = state
        this.setAttribute('data-count', String(total))
        this.setAttribute('data-products', String(productCount))
        this.setAttribute('data-step', String(Math.min(productCount, 3)))
        this.setAttribute('data-ready', full ? 'true' : 'false')
        this.setAttribute('data-unlocked', unlocked ? 'true' : 'false')
        this.setAttribute('data-state', state)
        this.renderSlots(groups, state)
        this.renderWishModal(items)
        this.renderCta(state, productCount)
        this.renderNote(state, productCount)
        this.renderGifts(state)
        this.renderCardButtons(full, pidSet, state !== 'created')
      }

      renderSlots(groups, state) {
        var slots = this.querySelectorAll(SLOT_SELECTOR)
        var reservedIndex = slots.length - 1
        var closable = state !== 'created'

        for (var i = 0; i < slots.length; i++) {
          var slot = slots[i]
          var group = groups[i]
          var thumb = slot.querySelector('.bfw__slot-thumb')
          var label = slot.querySelector('.bfw__slot-label')
          var priceEl = slot.querySelector('[data-bfw-slot-price]')
          var qtyEl = slot.querySelector('[data-bfw-slot-qty]')
          var closeBtn = slot.querySelector(SLOT_REMOVE_SELECTOR)

          if (i === reservedIndex) slot.hidden = groups.length < slots.length

          if (!group) {
            if (closeBtn) {
              closeBtn.hidden = true
              closeBtn.removeAttribute('aria-label')
            }
            slot.classList.remove('is--filled')
            slot.removeAttribute('data-pid')
            slot.removeAttribute('data-uids')
            slot.removeAttribute('data-qty')
            slot.removeAttribute('data-variant-count')
            slot.removeAttribute('title')
            if (thumb) thumb.textContent = ''
            if (label && this.slotLabel) label.textContent = this.slotLabel
            if (priceEl) priceEl.textContent = ''
            if (qtyEl) qtyEl.textContent = ''
            continue
          }

          slot.classList.add('is--filled')
          if (closeBtn) {
            closeBtn.hidden = !closable
            var removeLabel = this.removeLabel || 'Remove'
            var closeName = (group.title || '').replace(/\s+/g, ' ').trim()
            closeBtn.setAttribute('aria-label', closeName ? removeLabel + ': ' + closeName : removeLabel)
          }
          slot.setAttribute('data-pid', group.pid)
          slot.setAttribute('data-uids', group.uids.join(','))
          slot.setAttribute('data-qty', String(group.qty))
          slot.setAttribute('data-variant-count', String(group.items.length))
          slot.setAttribute('title', group.detailLabel)
          if (label) {
            label.textContent = group.title || this.slotLabel
          }
          if (priceEl) priceEl.textContent = group.price_html
          if (qtyEl) qtyEl.textContent = group.qty > 0 ? 'x' + group.qty : ''

          if (!thumb) continue
          if (!group.image) {
            thumb.textContent = ''
            continue
          }
          var img = thumb.querySelector('img')
          if (!img) {
            thumb.textContent = ''
            img = document.createElement('img')
            img.alt = ''
            img.loading = 'lazy'
            thumb.appendChild(img)
          }
          if (img.getAttribute('src') !== group.image) img.src = group.image
        }
      }

      renderCardButtons(full, pidSet, editable) {
        var cards = document.querySelectorAll(CARD_SELECTOR)
        var products = ensureCatalog().products

        for (var i = 0; i < cards.length; i++) {
          var pid = pidKey(cards[i].getAttribute('data-product-id'))
          var btn = cards[i].querySelector(CARD_BTN_SELECTOR)
          if (!pid || !btn) continue

          var product = products[pid]
          var soldOut = btn.classList.contains('is--soldout') || (!!product && product.available === false)
          var fullBlocked = !editable || (full && !(pidSet ? pidSet[pid] : hasProduct(pid)))
          var disabled = soldOut || fullBlocked
          btn.classList.toggle('is--soldout', soldOut)
          btn.classList.toggle('is--disabled', fullBlocked && !soldOut)
          btn.removeAttribute('disabled')
          if (disabled) btn.setAttribute('aria-disabled', 'true')
          else btn.removeAttribute('aria-disabled')
        }
      }

      submitCta(event, source) {
        if (event) event.preventDefault()
        var state = this.state
        var from = source || 'strip-cta'

        if (state === 'selecting') return

        if (state === 'created') {
          openRewardDialog('duplicate', 'duplicate')
          return
        }

        if (state === 'editing') {
          emit(
            EVENTS.update,
            { data: read(), items: getItems(), count: count(), source: from },
            this,
            true
          )
          this.render()
          return
        }

        if (createPending) return
        createPending = true
        var data = read()
        var detail = { data: data, items: getItems(data), count: count(data), source: from }
        var created = emit(EVENTS.create, detail, this, true)
        if (created.defaultPrevented) {
          this.render()
          return
        }
        createPending = false
        setCreated(true)
        this.render()
        if (this.createHref) window.location.href = this.createHref
      }

      renderWishModal(items) {
        var modal = document.querySelector(WISH_MODAL_SELECTOR)
        if (modal) modal.setAttribute('data-bfw-wish-state', this.state || '')

        var groupEls = document.querySelectorAll(WISH_GROUP_SELECTOR)
        if (!groupEls.length) return

        var removeLabel = this.removeLabel || 'Remove'
        var closable = this.state !== 'created'

        var filled = groupByProduct(items)

        var visible = Math.max(WISH_SLOTS_MIN, filled.length)
        if (visible > groupEls.length) visible = groupEls.length

        var slotLabel = this.slotLabel || ''
        for (var i = 0; i < groupEls.length; i++) {
          var groupEl = groupEls[i]
          var group = i < visible ? filled[i] : null
          var titleEl = groupEl.querySelector('.bfw__wish-group-title')
          var emptyEl = groupEl.querySelector('.bfw__wish-group-empty')
          var box = groupEl.querySelector('.bfw__wish-rows')

          groupEl.hidden = i >= visible
          if (titleEl) titleEl.textContent = group ? group.title || slotLabel : slotLabel
          if (!box) continue

          box.textContent = ''
          if (group) box.appendChild(buildWishSlot(group, removeLabel, closable))
          box.hidden = !group
          if (emptyEl) emptyEl.hidden = !!group
        }

        var seps = modal ? modal.querySelectorAll(WISH_SEP_SELECTOR) : []
        for (var s = 0; s < seps.length; s++) seps[s].hidden = s + 1 >= visible
      }

      renderCta(state, productCount) {
        if (!this.cta) return

        var labels = this.ctaLabels || {}
        var labelKey = CTA_LABEL_BY_STATE[state] || 'idle'
        var loading = createPending || submitPending || hydratePending
        var enough = (productCount || 0) >= UNLOCK_PRODUCTS
        var enabled = (state === 'ready' || state === 'created' || (state === 'editing' && enough)) && !loading
        var dim = state === 'selecting' || (state === 'editing' && !enough)
        updateCta(this.cta, labels[labelKey], enabled, state, dim)

        var modalCta = document.querySelector(WISH_CTA_SELECTOR)
        if (modalCta) updateCta(modalCta, labels[labelKey], enabled, state, dim)
      }

      renderNote(state, productCount) {
        var note = this.querySelector(NOTE_SELECTOR)
        if (!note) return

        var createdState = state === 'created' || state === 'editing'
        var labelEl = note.querySelector('.bfw__strip-note-label')
        var recordBtn = note.querySelector(RECORD_SELECTOR)
        var editBtn = note.querySelector(EDIT_SELECTOR)

        if (labelEl) {
          var labels = this.noteLabels || []
          var index = createdState ? -1 : Math.min(productCount || 0, 3)
          var text = index < 0 ? '' : labels[index] || ''
          labelEl.textContent = text
          labelEl.hidden = !text
        }
        note.hidden = !createdState && !(labelEl && labelEl.textContent)

        var recordId = createdState ? findRewardDialog('record', getRewardKind()) : ''

        if (recordBtn) {
          recordBtn.hidden = !createdState
          if (recordId && recordBtn.getAttribute('data-dsm-dialog-open') !== recordId) {
            recordBtn.setAttribute('data-dsm-dialog-open', recordId)
          }
        }

        var recordHead = this.querySelector(RECORD_HEAD_SELECTOR)
        if (recordHead) {
          recordHead.hidden = !createdState
          if (recordId && recordHead.getAttribute('data-dsm-dialog-open') !== recordId) {
            recordHead.setAttribute('data-dsm-dialog-open', recordId)
          }
        }
        if (editBtn) {
          var editLocked = state === 'editing'
          editBtn.hidden = !createdState
          if (editBtn.disabled !== editLocked) editBtn.disabled = editLocked
          editBtn.setAttribute('aria-disabled', editLocked ? 'true' : 'false')
        }
      }

      renderGifts(state) {
        var gifts = document.querySelectorAll(GIFTS_SELECTOR)

        for (var i = 0; i < gifts.length; i++) {
          var textEl = gifts[i].querySelector(GIFTS_TEXT_SELECTOR)
          if (!textEl) continue
          var base = decodeEntities(gifts[i].getAttribute('data-gifts-text')) || ''
          var created = decodeEntities(gifts[i].getAttribute('data-gifts-text-created')) || ''
          var next = state === 'created' || state === 'editing' ? created || base : base
          if (textEl.textContent !== next) textEl.textContent = next
          gifts[i].hidden = !next
        }
      }

      showToast(text, variant) {
        if (!this.toast || !this.toastText) return
        var message = text || this.toastLabel
        if (!message) return

        this.toastText.textContent = message
        this.toast.classList.toggle('is--warning', variant === 'warning')
        this.toast.hidden = false
        void this.toast.offsetWidth
        this.toast.classList.add('is--visible')

        if (this._toastTimer) window.clearTimeout(this._toastTimer)
        this._toastTimer = window.setTimeout(
          function () {
            this._toastTimer = 0
            this.hideToast()
          }.bind(this),
          TOAST_DURATION
        )
      }

      showFullToast() {
        if (this.state === 'created') return
        this.showToast(this.toastFullLabel || this.toastLabel, 'warning')
      }

      hideToast() {
        if (!this.toast) return
        this.toast.classList.remove('is--visible')
        if (this._toastHideTimer) window.clearTimeout(this._toastHideTimer)
        this._toastHideTimer = window.setTimeout(
          function () {
            this._toastHideTimer = 0
            if (!this.toast.classList.contains('is--visible')) this.toast.hidden = true
          }.bind(this),
          TOAST_FADE
        )
      }
    }

    var API_CONFIG_ID = 'bfw-wishlist-api-config'
    var WISHLIST_API_PATH = '/api/station/activity/wishlist/'
    var DEFAULT_ACTIVITY_CODE = 'WISHLIST_2026'
    var PENDING_SAVE_KEY = 'bfw:wishlist:pending-save'
    var SECTION_ANCHOR_ID = 'bfw'
    var API_TIMEOUT = 15000
    var REWARD_KIND_BY_TYPE = { 1: 'gift-card', 2: 'bundle', 3: 'bundle-2' }

    var apiConfigCache = null
    var apiBusy = false
    var hydratedToken = ''

    function readApiConfig() {
      if (apiConfigCache) return apiConfigCache
      var config = { activityCode: DEFAULT_ACTIVITY_CODE, registerSource: '', saveErrorText: '', saveErrorByCode: {} }
      var node = document.getElementById(API_CONFIG_ID)
      if (node) {
        try {
          var json = JSON.parse(node.textContent || '{}') || {}
          if (json.activityCode) config.activityCode = String(json.activityCode).trim() || DEFAULT_ACTIVITY_CODE
          if (json.registerSource) config.registerSource = String(json.registerSource).trim()
          if (json.saveErrorText) config.saveErrorText = String(json.saveErrorText).trim()
          var byCode = json.saveErrorByCode || {}
          Object.keys(byCode).forEach(function (code) {
            var text = String(byCode[code] || '').trim()
            if (text) config.saveErrorByCode[code] = text
          })
        } catch (error) {
          emit(EVENTS.error, { reason: 'api-config-parse-failed', detail: String(error) })
        }
      }
      apiConfigCache = config
      return config
    }

    function mcConfig() {
      return window.__MC__CONFIG__ || {}
    }

    function getToken() {
      var name = mcConfig().cookieName || 'momcozy-token'
      var parts = document.cookie ? document.cookie.split('; ') : []
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(name + '=') === 0) {
          return decodeURIComponent(parts[i].slice(name.length + 1)) || ''
        }
      }
      return ''
    }

    function isLoggedIn() {
      return !!getToken()
    }

    function unwrapApiPayload(payload) {
      if (!payload || typeof payload !== 'object') return payload
      var code = payload.code
      if (code !== undefined && code !== null && String(code) !== '200') {
        var error = new Error(payload.message || payload.msg || ('接口返回 ' + code))
        error.code = code
        throw error
      }
      if (payload.result !== undefined) return payload.result
      if (payload.data !== undefined) return payload.data
      return payload
    }

    function apiRequest(method, path, body) {
      var base = String(mcConfig().apiUrl || '').replace(/\/+$/, '')
      if (!base) return Promise.reject(new Error('api-url-missing'))

      var headers = { 'Content-Type': 'application/json' }
      var extra = mcConfig().headers || {}
      Object.keys(extra).forEach(function (key) {
        headers[key] = extra[key]
      })
      var token = getToken()
      if (token) headers.Authorization = 'Bearer ' + token

      var options = { method: method, headers: headers, credentials: 'omit' }
      if (body) options.body = JSON.stringify(body)

      var timer = 0
      var controller = window.AbortController ? new window.AbortController() : null
      if (controller) {
        options.signal = controller.signal
        timer = window.setTimeout(function () {
          controller.abort()
        }, API_TIMEOUT)
      }

      return fetch(base + path, options)
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return null
            })
            .then(function (payload) {
              return { ok: response.ok, status: response.status, payload: payload }
            })
        })
        .then(function (result) {
          if (timer) window.clearTimeout(timer)
          if (!result.ok && (!result.payload || result.payload.code === undefined)) {
            throw new Error('HTTP ' + result.status)
          }
          return unwrapApiPayload(result.payload)
        })
        .catch(function (error) {
          if (timer) window.clearTimeout(timer)
          throw error
        })
    }

    function fetchRemoteWishlist() {
      return apiRequest('GET', WISHLIST_API_PATH + encodeURIComponent(readApiConfig().activityCode))
    }

    function saveRemoteWishlist(body) {
      return apiRequest(
        'POST',
        WISHLIST_API_PATH + encodeURIComponent(readApiConfig().activityCode) + '/save',
        body
      )
    }

    function fetchRewards() {
      return apiRequest(
        'GET',
        WISHLIST_API_PATH + encodeURIComponent(readApiConfig().activityCode) + '/rewards'
      )
    }

    function cartItems(items) {
      var merged = []
      ;(items || []).forEach(function (item) {
        var id = toId(item.skuId)
        if (!id) return
        for (var i = 0; i < merged.length; i++) {
          if (merged[i].id === id) {
            merged[i].quantity += toQty(item.quantity)
            return
          }
        }
        merged.push({ id: id, quantity: toQty(item.quantity) })
      })
      return merged
    }

    function notifyCartUpdated() {
      try {
        if (
          window.T4SThemeSP &&
          window.T4SThemeSP.Cart &&
          typeof window.T4SThemeSP.Cart.getToFetch === 'function'
        ) {
          window.T4SThemeSP.Cart.getToFetch()
          return
        }
      } catch (error) {}
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh:opend', { bubbles: true })
      )
    }

    function addItemsToCart(items) {
      var payload = cartItems(items)
      if (!payload.length) return Promise.resolve(false)
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
      return fetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload }),
      }).then(function (response) {
        if (!response.ok) throw new Error('cart-add-' + response.status)
        return response.json().then(function (data) {
          notifyCartUpdated()
          return data
        })
      })
    }

    function buildApiItems(data) {
      var source = data || read()
      var items = []
      Object.keys(source).forEach(function (pid) {
        source[pid].forEach(function (entry) {
          var item = { spuId: String(pid), skuId: String(entry.uid), quantity: toQty(entry.qty) }
          var handle = toHandle(entry.handle) || catalogHandle(pid)
          if (handle) item.handle = handle
          items.push(item)
        })
      })
      return items
    }

    function parseRemoteItems(remote) {
      var wishlist = remote && remote.wishlist ? remote.wishlist : remote
      var raw = wishlist && wishlist.itemsJson
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw)
        } catch (error) {
          return {}
        }
      }
      if (raw && !Array.isArray(raw) && Array.isArray(raw.items)) raw = raw.items
      if (!Array.isArray(raw)) return {}

      var data = Object.create(null)
      raw.forEach(function (entry) {
        if (!entry || typeof entry !== 'object') return
        var pid = pidKey(entry.spuId || entry.spu_id)
        var uid = toId(entry.skuId || entry.sku_id)
        if (!pid || !uid) return
        var handle = toHandle(entry.handle || entry.spuHandle || entry.spu_handle)
        if (!data[pid]) {
          if (Object.keys(data).length >= MAX_PRODUCTS) return
          data[pid] = []
        }
        var item = { uid: uid, qty: toQty(entry.quantity || entry.qty) }
        if (handle) item.handle = handle
        data[pid].push(item)
      })
      return data
    }

    function setPendingSave(mode) {
      try {
        if (mode) window.sessionStorage.setItem(PENDING_SAVE_KEY, mode)
        else window.sessionStorage.removeItem(PENDING_SAVE_KEY)
      } catch (error) {
      }
    }

    function getPendingSave() {
      try {
        return window.sessionStorage.getItem(PENDING_SAVE_KEY) || ''
      } catch (error) {
        return ''
      }
    }

    // 登录回跳地址带 section 锚点：第三方登录会整页跳到 /pages/third-login-auth，
    // 成功后 snippets/third-login-auth.liquid 用 location.href = redirect 跳回，
    // 浏览器据此自动定位到愿望清单 section（偏移由 CSS scroll-margin-top 兜住）。
    function buildLoginRedirectUrl() {
      var base = window.location.href.split('#')[0]
      return base + '#' + SECTION_ANCHOR_ID
    }

    function openLoginDialog() {
      var dialog = document.querySelector('login-dialog')
      if (!dialog || typeof dialog.open !== 'function') {
        emit(EVENTS.error, { reason: 'login-dialog-unavailable' })
        return false
      }
      var registerSource = readApiConfig().registerSource
      var options = { redirect: buildLoginRedirectUrl() }
      if (registerSource) options.activityType = registerSource
      dialog.open(null, options)
      return true
    }

    function setRecordLoading(loading) {
      var buttons = document.querySelectorAll(RECORD_SELECTOR + ',' + RECORD_HEAD_SELECTOR)
      for (var i = 0; i < buttons.length; i++) {
        var button = buttons[i]
        button.classList.toggle('is-loading', !!loading)
        if (loading) {
          button.setAttribute('disabled', '')
          button.setAttribute('aria-busy', 'true')
        } else {
          button.removeAttribute('disabled')
          button.removeAttribute('aria-busy')
        }
      }
    }

    function showRewardsErrorToast(error) {
      var message = readApiConfig().saveErrorText || (error && error.message) || ''
      var strips = document.querySelectorAll('bfw-strip')
      for (var i = 0; i < strips.length; i++) {
        if (typeof strips[i].showToast !== 'function') continue
        strips[i].showToast(message || strips[i].toastErrorLabel, 'warning')
      }
    }

    function openRecordDialog() {
      if (recordPending) return

      if (!isLoggedIn()) {
        openLoginDialog()
        return
      }

      var kind = getRewardKind()
      if (rewardsResolved || kind) {
        openRewardDialog(kind, 'record')
        return
      }

      recordPending = true
      setRecordLoading(true)
      fetchRewards()
        .then(function (reward) {
          rewardsResolved = true
          recordPending = false
          setRecordLoading(false)
          var resolved = REWARD_KIND_BY_TYPE[(reward && reward.rewardType) || 0] || ''
          if (resolved) setRewardKind(resolved)
          renderStrips()
          openRewardDialog(resolved || kind, 'record')
        })
        .catch(function (error) {
          recordPending = false
          setRecordLoading(false)
          showRewardsErrorToast(error)
          emit(EVENTS.error, {
            reason: 'rewards-load-failed',
            detail: { code: (error && error.code) || '', message: (error && error.message) || '' },
          })
        })
    }

    function onRecordClick(event) {
      var target = event.target
      var button =
        target && target.closest ? target.closest(RECORD_SELECTOR + ',' + RECORD_HEAD_SELECTOR) : null
      if (!button || button.hidden || button.hasAttribute('disabled')) return

      event.preventDefault()
      event.stopPropagation()
      openRecordDialog()
    }

    function resetCreatedOnLogout(source) {
      var state = readState()
      if (!state.created) return false

      rewardKind = ''
      rewardsResolved = false
      hydratedToken = ''
      writeState({ items: state.items, created: false, account: '' })
      renderStrips()
      emit(EVENTS.change, { data: read(), items: getItems(), count: count(), source: source })
      log('logout-reset', { source: source })
      return true
    }

    function onAuthLogout() {
      resetCreatedOnLogout('logout')
    }

    function reconcileLoggedOutState() {
      var state = readState()
      if (!state.created || !state.account || isLoggedIn()) return false
      return resetCreatedOnLogout('session-check')
    }

    function showSaveErrorToast(error) {
      var config = readApiConfig()
      var code = error && error.code !== undefined && error.code !== null ? String(error.code) : ''
      var message =
        (code && config.saveErrorByCode[code]) || config.saveErrorText || (error && error.message) || ''
      var strips = document.querySelectorAll('bfw-strip')
      var shown = false
      for (var i = 0; i < strips.length; i++) {
        if (typeof strips[i].showToast !== 'function') continue
        strips[i].showToast(message || strips[i].toastErrorLabel, 'warning')
        shown = true
      }
      return shown
    }

    function failSave(mode, error) {
      if (mode !== 'update') failCreate()
      setSubmitPending(false)
      showSaveErrorToast(error)
      emit(EVENTS.error, {
        reason: 'wishlist-save-failed',
        detail: {
          mode: mode,
          code: (error && error.code) || '',
          message: (error && error.message) || '',
        },
      })
      log('save-failed', {
        mode: mode,
        code: (error && error.code) || '',
        message: (error && error.message) || '',
      })
    }

    function submitToApi(mode) {
      if (apiBusy) return

      if (!isLoggedIn()) {
        setPendingSave(mode)
        if (mode !== 'update') failCreate()
        if (!openLoginDialog()) {
          setPendingSave('')
          failSave(mode, null)
        }
        return
      }

      var items = buildApiItems()
      if (!items.length) {
        // 本地清单为空时不提示错误（新账号登录后远端无清单属正常情况）
        setPendingSave('')
        if (mode !== 'update') failCreate()
        setSubmitPending(false)
        log('save-skipped-empty-items', { mode: mode })
        return
      }

      var token = getToken()
      if (mode === 'create' && token && token !== hydratedToken) {
        hydratedToken = token
        hydratePending = true
        renderStrips()
        fetchRemoteWishlist()
          .then(function (remote) {
            hydratePending = false
            var filled = applyRemoteWishlist(remote)
            renderStrips()
            if (filled) {
              failCreate()
              return
            }
            submitToApi(mode)
          })
          .catch(function () {
            hydratePending = false
            renderStrips()
            submitToApi(mode)
          })
        return
      }

      apiBusy = true
      setPendingSave('')
      if (mode !== 'update') createPending = true
      setSubmitPending(true)

      saveRemoteWishlist({ activityCode: readApiConfig().activityCode, items: items })
        .then(function () {
          if (mode === 'update') {
            apiBusy = false
            finishUpdate()
            return
          }
          addItemsToCart(items)
            .catch(function (error) {
              if (window.console) window.console.warn('[bfw-strip] cart add failed', error)
            })
            .then(function () {
              fetchRewards()
                .then(function (reward) {
                  apiBusy = false
                  rewardsResolved = true
                  var kind = REWARD_KIND_BY_TYPE[(reward && reward.rewardType) || 0]
                  completeCreate(kind ? { kind: kind } : { duplicate: true })
                })
                .catch(function () {
                  apiBusy = false
                  completeCreate({ duplicate: true })
                })
            })
        })
        .catch(function (error) {
          apiBusy = false
          failSave(mode, error)
        })
    }

    function onApiCreate(event) {
      event.preventDefault()
      submitToApi('create')
    }

    function onApiUpdate(event) {
      event.preventDefault()
      submitToApi('update')
    }

    function applyRemoteWishlist(remote) {
      syncAccount()
      var items = parseRemoteItems(remote)
      if (!Object.keys(items).length) return false
      setItems(items, 'api-hydrate')
      if (!isCreated()) {
        setCreated(true)
        renderStrips()
      }
      fetchRewards()
        .then(function (reward) {
          rewardsResolved = true
          var kind = REWARD_KIND_BY_TYPE[(reward && reward.rewardType) || 0]
          if (!kind || kind === getRewardKind()) return
          setRewardKind(kind)
          renderStrips()
        })
        .catch(function () {
        })
      return true
    }

    function onApiLogin() {
      if (apiBusy) return

      syncAccount()

      var mode = getPendingSave()
      setPendingSave('')

      var token = getToken()
      if (!token || token === hydratedToken) {
        if (mode) submitToApi(mode)
        return
      }
      hydratedToken = token

      hydratePending = true
      renderStrips()
      fetchRemoteWishlist()
        .then(function (remote) {
          hydratePending = false
          var filled = applyRemoteWishlist(remote)
          renderStrips()
          if (!filled && mode) submitToApi(mode)
        })
        .catch(function () {
          hydratePending = false
          renderStrips()
          if (mode) submitToApi(mode)
        })
    }

    function init() {
      if (!customElements.get('bfw-strip')) customElements.define('bfw-strip', BfwStrip)

      if (document.querySelector('bfw-strip[data-debug]')) debug = true

      document.addEventListener('cart:variant-update', onVariantUpdate)

        ;[EVENTS.toggle, EVENTS.add, EVENTS.remove, EVENTS.clear].forEach(function (name) {
          document.addEventListener(name, onWishlistEvent)
        })

      document.addEventListener('click', onDocumentClick)

      document.addEventListener('click', onCardButtonClick, true)

      document.addEventListener(EVENTS.create, onApiCreate)
      document.addEventListener(EVENTS.update, onApiUpdate)
      document.addEventListener('LUTE:AUTH:LOGIN', onApiLogin)
      document.addEventListener('lute:account:logout', onAuthLogout)
      document.addEventListener('LUTE:AUTH:LOGOUT', onAuthLogout)
      document.addEventListener('click', onRecordClick, true)

      reconcileLoggedOutState()

      log('initialized')
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true })
    } else {
      init()
    }
  })()
